import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Divider,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { brandColors } from '../../theme';

type ReportType = 'performance' | 'spend' | 'orders' | 'providers';

const dimensions = ['Kunde', 'Kanal', 'Kategorie', 'Zeitraum'] as const;
const metrics = ['Impressions', 'Clicks', 'Conversions', 'ROAS', 'CPA', 'Ausgaben', 'Bestellungen'] as const;

const ReportEditor: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('performance');
  const [dateFrom, setDateFrom] = useState('2025-08-01');
  const [dateTo, setDateTo] = useState('2025-09-22');
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>(['Zeitraum']);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['Impressions', 'Clicks']);
  const [format, setFormat] = useState<'csv' | 'xlsx' | 'pdf'>('csv');

  const previewRows = useMemo(() => {
    const rows = Array.from({ length: 6 }).map((_, i) => ({
      Zeitraum: `2025-09-${(i + 1).toString().padStart(2, '0')}`,
      Kunde: ['Alle', 'Kunde A', 'Kunde B'][i % 3],
      Kanal: ['Meta', 'Google Ads', 'Print'][i % 3],
      Kategorie: ['WM1', 'WM5', 'WM7'][i % 3],
      Impressions: Math.round(90000 + Math.random() * 20000).toLocaleString(),
      Clicks: Math.round(4000 + Math.random() * 1500).toLocaleString(),
      Conversions: Math.round(250 + Math.random() * 100).toLocaleString(),
      ROAS: (3 + Math.random() * 2).toFixed(2),
      CPA: (5 + Math.random() * 2).toFixed(2),
      Ausgaben: `€${Math.round(1500 + Math.random() * 600).toLocaleString()}`,
      Bestellungen: Math.round(1 + Math.random() * 5),
    }));
    return rows;
  }, [dateFrom, dateTo, reportType]);

  const handleDownload = () => {
    const payload = {
      type: reportType,
      range: { from: dateFrom, to: dateTo },
      dimensions: selectedDimensions,
      metrics: selectedMetrics,
      format,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${reportType}.${format === 'pdf' ? 'json' : format}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 1 }}>Report Editor</Typography>
        <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
          Erstellen und exportieren Sie Berichte aus der imaginären Datenbank.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Konfiguration</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Report Typ</InputLabel>
                <Select value={reportType} label="Report Typ" onChange={(e) => setReportType(e.target.value as ReportType)}>
                  <MenuItem value="performance">Performance</MenuItem>
                  <MenuItem value="spend">Ausgaben</MenuItem>
                  <MenuItem value="orders">Bestellungen</MenuItem>
                  <MenuItem value="providers">Service Partner</MenuItem>
                </Select>
              </FormControl>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Von" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Bis" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
              </Grid>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Dimensionen</InputLabel>
                <Select
                  multiple
                  value={selectedDimensions}
                  label="Dimensionen"
                  onChange={(e) => setSelectedDimensions(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {dimensions.map((d) => (
                    <MenuItem key={d} value={d}>
                      <Checkbox checked={selectedDimensions.indexOf(d) > -1} />
                      <ListItemText primary={d} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Metriken</InputLabel>
                <Select
                  multiple
                  value={selectedMetrics}
                  label="Metriken"
                  onChange={(e) => setSelectedMetrics(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((v) => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {metrics.map((m) => (
                    <MenuItem key={m} value={m}>
                      <Checkbox checked={selectedMetrics.indexOf(m) > -1} />
                      <ListItemText primary={m} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Format</InputLabel>
                <Select value={format} label="Format" onChange={(e) => setFormat(e.target.value as 'csv' | 'xlsx' | 'pdf')}>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="xlsx">Excel (XLSX)</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" sx={{ mt: 2, bgcolor: brandColors.electricTeal }} onClick={handleDownload}>Report herunterladen</Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Vorschau (Beispieldaten)</Typography>
              <Box sx={{ border: '1px solid var(--line)', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(120px, 1fr))', bgcolor: 'var(--slate-50)', borderBottom: '1px solid var(--line)' }}>
                  {['Zeitraum', 'Kunde', 'Kanal', 'Kategorie', 'Impressions', 'Clicks', 'Conversions', 'Ausgaben'].map((h) => (
                    <Box key={h} sx={{ p: 1, fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>{h}</Box>
                  ))}
                </Box>
                {previewRows.map((r, idx) => (
                  <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(120px, 1fr))', borderBottom: '1px solid var(--line)' }}>
                    {['Zeitraum', 'Kunde', 'Kanal', 'Kategorie', 'Impressions', 'Clicks', 'Conversions', 'Ausgaben'].map((k) => (
                      <Box key={k} sx={{ p: 1, fontSize: 13 }}>{(r as any)[k]}</Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportEditor;



